/*
 * Un singur hook pentru "cere de la api, arata incarcare, arata eroare".
 *
 * Exista pentru ca fiecare ecran are aceleasi trei stari, iar cerinta e ca
 * niciun ecran sa nu ramana mut cand api-ul cade. Scris de mana in fiecare
 * ecran, pasul care lipseste e mereu acelasi: cel de eroare.
 *
 * Nu e o biblioteca de cache. Nu memoreaza intre ecrane si nu deduplica: n-ar
 * ajuta aici (fiecare ecran cere altceva) si ar aduce un al doilea adevar
 * despre ce e in cos.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, toApiError } from '../api/errors';

export type AsyncStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AsyncResult<T> {
  status: AsyncStatus;
  data: T | null;
  error: ApiError | null;
  /** Reincearca. Butonul din starea de eroare cheama asta, nu o remontare de ecran. */
  reload: () => void;
}

export function useAsync<T>(
  task: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncResult<T> {
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  /* Functia se schimba la fiecare redesenare, deci nu poate sta in `deps` fara
     sa produca o bucla. Se tine intr-un ref si se ruleaza dupa `deps` declarate
     de apelant - acelasi contract ca la useEffect. */
  const taskRef = useRef(task);
  taskRef.current = task;

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    setStatus('loading');
    setError(null);

    taskRef.current(controller.signal).then(
      (value) => {
        if (!alive) return;
        setData(value);
        setStatus('ready');
      },
      (cause: unknown) => {
        /* Ecran parasit sau cerere inlocuita: nu e o eroare de aratat nimanui. */
        if (!alive || controller.signal.aborted) return;
        setError(toApiError(cause));
        setStatus('error');
      },
    );

    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { status, data, error, reload };
}
